import React, { useEffect, useMemo, useState } from "react";
import { useForm, useWatch } from "react-hook-form";
import { yupResolver } from "@hookform/resolvers/yup";
import { useDispatch } from "react-redux";
import { useNavigate, useParams } from "react-router-dom";
import * as yup from "yup";
import { DateTime } from "luxon";
import { AlertCircle, Clock, DollarSign } from "lucide-react";

import { getProfile } from "../../services/mentorProfile";
import { getMentorSheduleAppointments } from "../../services/sheduleAppointmentsServices";

import CommonDropdown from "../commonDropDown/CommonDropDown";
import CommonTextField from "../commonTextField/CommonTextField";
import CustomButton from "../customButtom/CustomButton";
import CalenderSelector from "../Selectors/CalenderSelector";

// Schema
const schema = yup.object().shape({
  consultingFee: yup.string().required("Booking fee is required"),
  time: yup.string().required("Time selection is required"),
  comments: yup.string().max(500).required(),
  date: yup.string().required(),
  selectedTime: yup.string().required("Please select a time slot"),
});

function BookSessionForm({ timeZone, setIsOpen, userData }) {
  const { userId } = useParams();
  const navigate = useNavigate();
  const dispatch = useDispatch();

  const [mentorProfile, setMentorProfile] = useState(null);
  const [slotsData, setSlotsData] = useState([]);
  const [selectedDate, setSelectedDate] = useState(null);
  const [localTimeZone, setLocalTimeZone] = useState("");

  const {
    register,
    handleSubmit,
    setValue,
    control,
    watch,
    formState: { errors },
  } = useForm({
    resolver: yupResolver(schema),
    defaultValues: {
      time: "",
      comments: "",
      selectedTime: "",
    },
  });

  const selectedDuration = useWatch({ control, name: "time" });

  // Format selected JS Date
  const formatDate = (date) => date && DateTime.fromJSDate(date).toFormat("yyyy-MM-dd");

  const fetchMentorSlots = async () => {
    try {
      const data = await getMentorSheduleAppointments(userId);
      if (!data?.length) return setSlotsData([]);

      const converted = data.map((appt) => {
        const start = DateTime.fromFormat(appt.startTime, "yyyy-MM-dd HH:mm:ss 'UTC'", { zone: "UTC" }).setZone(localTimeZone);
        const end = DateTime.fromFormat(appt.endTime, "yyyy-MM-dd HH:mm:ss 'UTC'", { zone: "UTC" }).setZone(localTimeZone);
        return {
          id: appt._id,
          date: start.toISODate(),
          startTime: start.toFormat("HH:mm"),
          endTime: end.toFormat("HH:mm"),
        };
      });

      const grouped = converted.reduce((acc, slot) => {
        const day = acc.find((d) => d.date === slot.date) || { date: slot.date, slots: [] };
        if (!acc.includes(day)) acc.push(day);
        day.slots.push(slot);
        return acc;
      }, []);

      const today = DateTime.local().startOf("day");
      const filtered = grouped.filter((g) => DateTime.fromISO(g.date) >= today);

      setSlotsData(filtered);
    } catch {
      setSlotsData([]);
    }
  };

  const fetchMentorProfile = async () => {
    try {
      const profile = await dispatch(getProfile({ userId }));
      setMentorProfile(profile || null);
    } catch {
      setMentorProfile(null);
    }
  };

  // Get Available Time Slots
  const getGroupedSlots = (slots, duration = 15) => {
    if (!slots.length) return [];

    slots.sort((a, b) => DateTime.fromFormat(a.startTime, "HH:mm") - DateTime.fromFormat(b.startTime, "HH:mm"));
    const needed = parseInt(duration, 10) / 15 || 1;

    if (needed <= 1) return slots.map((s) => `${s.startTime} - ${s.endTime}`);

    const available = [];
    for (let i = 0; i <= slots.length - needed; i++) {
      let ok = true;
      for (let j = 0; j < needed - 1; j++) {
        const a = DateTime.fromFormat(slots[i + j].endTime, "HH:mm");
        const b = DateTime.fromFormat(slots[i + j + 1].startTime, "HH:mm");
        if (!a.equals(b)) {
          ok = false;
          break;
        }
      }
      if (ok) available.push(`${slots[i].startTime} - ${slots[i + needed - 1].endTime}`);
    }
    return available;
  };

  const availableSlots = useMemo(() => {
    if (!selectedDate) return [];

    const dateStr = formatDate(selectedDate);
    const day = slotsData.find((d) => d.date === dateStr);
    if (!day) return [];

    let grouped = getGroupedSlots(day.slots, selectedDuration.split("min")[0]);
    const now = DateTime.local().setZone(localTimeZone);

    return dateStr === now.toISODate()
      ? grouped.filter((slot) =>
          DateTime.fromFormat(`${dateStr} ${slot.split(" - ")[0]}`, "yyyy-MM-dd HH:mm", { zone: localTimeZone }) > now
        )
      : grouped;
  }, [selectedDate, slotsData, selectedDuration, localTimeZone]);

  const handleSlotSelection = (slot) => {
    setValue("selectedTime", slot);
  };

  const onSubmit = async (data) => {
    const dateStr = DateTime.fromISO(data.date).toISODate();
    const selectedRanges = data.selectedTime.split(", ");

    const flatSlots = slotsData.flatMap((day) =>
      day.slots.map((slot) => ({ ...slot, date: day.date }))
    );

    const slotIds = selectedRanges.flatMap((range) => {
      const [start, end] = range.split(" - ");
      return flatSlots
        .filter((s) => s.date === dateStr && s.startTime >= start && s.endTime <= end)
        .map((s) => s.id);
    });

    const payload = {
      mentorId: userId,
      paymentId: mentorProfile.paymentAccountId,
      slotIds,
    };

    navigate("/dashboard/checkout", {
      state: {
        formData: { ...data, selectedDate },
        userData,
        transformedData: payload,
      },
    });
  };

  // Effects
  useEffect(() => {
    setLocalTimeZone(Intl.DateTimeFormat().resolvedOptions().timeZone);
  }, []);

  useEffect(() => {
    if (localTimeZone) fetchMentorSlots();
  }, [localTimeZone]);

  useEffect(() => {
    fetchMentorProfile();
  }, [userId]);

  useEffect(() => {
    if (selectedDuration) {
      setValue("selectedTime", "");
      const fee = mentorProfile?.mentorMeeting?.[selectedDuration];
      if (fee) setValue("consultingFee", fee);
    }
  }, [selectedDuration, mentorProfile]);

  // Render
  return (
    <form className="bg-white rounded w-full text-xs p-2 flex flex-col gap-5" onSubmit={handleSubmit(onSubmit)}>
      <div className="flex justify-between w-full items-center py-4">
        <div>
          <p className="font-semibold text-base text-gray-800">Meeting Time Zone</p>
          <p className="text-sm text-gray-600">{timeZone}</p>
        </div>
        <div>
          <p className="font-semibold text-base text-gray-800">Your Time Zone</p>
          <p className="text-sm text-gray-600">{localTimeZone}</p>
        </div>
      </div>

      <CommonDropdown
        label="Time"
        name="time"
        register={register}
        required
        errors={errors}
        defaultValue={selectedDuration}
        options={[
          { value: "", label: "Select option" },
          { value: "15minmeeting", label: "15 min" },
          { value: "30minmeeting", label: "30 min" },
          { value: "45minmeeting", label: "45 min" },
          { value: "60minmeeting", label: "60 min" },
        ]}
        secondLabel={
          selectedDuration && mentorProfile?.mentorMeeting?.[selectedDuration] ? (
            <div className="flex space-x-4 text-sm font-medium text-gray-900">
              <span className="flex items-center space-x-1">
                <DollarSign size={14} />
                <span>{mentorProfile.mentorMeeting[selectedDuration]}</span>
              </span>
              <span className="flex items-center space-x-1">
                <Clock size={14} />
                <span>{selectedDuration === "trialMeeting" ? "Trial" : `${selectedDuration.split("min")[0]} min`}</span>
              </span>
            </div>
          ) : null
        }
      />

      {!selectedDuration ? (
        <div className="flex flex-col items-center justify-center bg-red-50 border border-red-300 text-red-600 rounded-lg p-6 my-4 shadow-sm space-y-2 h-40 text-center">
          <AlertCircle className="w-8 h-8 text-red-500" />
          <p className="text-sm font-medium">Please select a time to proceed.</p>
        </div>
      ) : (
        <>
          <CalenderSelector
            availableDates={slotsData}
            setSelectedDate={setSelectedDate}
            selectedDate={selectedDate}
            register={register}
            name="date"
            errors={errors}
            setValue={setValue}
          />

          <div className="grid grid-cols-3 xl:grid-cols-5 gap-1 h-20 overflow-auto border p-2 rounded-md">
            {availableSlots.length > 0 ? (
              availableSlots.map((slot, i) => {
                const selected = watch("selectedTime")?.split(", ") || [];
                const isSelected = selected.includes(slot);

                return (
                  <button
                    key={i}
                    type="button"
                    className={`p-1 border rounded text-xs ${
                      isSelected ? "bg-custom-blue text-white" : "hover:bg-gray-200 bg-gray-100"
                    }`}
                    onClick={() => handleSlotSelection(slot)}
                  >
                    {slot}
                  </button>
                );
              })
            ) : (
              <p className="text-gray-500 text-sm col-span-full text-center">
                {selectedDate
                  ? "No available slots for the selected date"
                  : "Please select a date to view available slots"}
              </p>
            )}
            <input type="hidden" {...register("selectedTime")} />
          </div>

          {errors.selectedTime && <p className="text-red-500">{errors.selectedTime.message}</p>}

          <CommonTextField
            type="text"
            name="comments"
            label="Comments"
            register={register}
            required
            errors={errors}
            placeholder="Add message"
          />

          <CustomButton
            type="submit"
            label="Proceed to Check Out"
            buttonClass="p-3 rounded bg-blue-500 text-white text-xs w-full bg-custom-blue"
          />
        </>
      )}
    </form>
  );
}

export default BookSessionForm;
